import { ensureWorkspaceDoc } from '@blocksuite/store';

import {
  _runCapture,
  cellDefaultValueGet,
  cellValueChange,
  cellValueChangeSharded,
  cellValueGet,
  cellValueGetFromLoadedRows,
  cellValuesChange,
  getCellsWindow,
  getLogicalRowIdsSnapshot,
  getProjectionSource,
  getRowsWindow,
  getSearchCandidateRowIds,
  newPropertyName,
  normalizeRawCellValue,
  normalizeSchemaLockedSelectValue,
  resolveRawValueForProperty,
  rowExists,
  serializeRawValueForProperty,
} from './data-source-cell-operations.js';
import {
  canMutatePropertySchema,
  collectDatabaseFileCellReviewCopyRows,
  dispatchDatabaseFileCellReviewCopy,
  getNormalPropertyAndIndex,
  getPropertyAndIndex,
  isTemporalProfileRoleProperty,
  propertyAdd,
  propertyCanDelete,
  propertyCanDuplicate,
  propertyDataGet,
  propertyDataSet,
  propertyDataTypeGet,
  propertyDelete,
  propertyDuplicate,
  propertyMetaGet,
  propertyNameGet,
  propertyNameSet,
  propertyReadonlyGet,
  propertyTypeCanSet,
  propertyTypeGet,
  propertyTypeSet,
  propertyTypeSetSharded,
  readTemporalProfileViewPath,
  updateSourceProperty,
} from './data-source-property-operations.js';
import { getDatabaseWriterPorts } from './data-source-publication.js';
import {
  getShardedInsertIndex,
  rowAdd,
  rowDelete,
  rowDeleteSharded,
  rowMove,
  viewDataAdd,
  viewDataDelete,
  viewDataDuplicate,
  viewDataGet,
  viewDataMoveTo,
  viewDataUpdate,
  viewMetaGet,
  viewMetaGetById,
} from './data-source-row-view-operations.js';
import type {
  DatabaseSourceHost,
  DatabaseSourceRuntime,
} from './data-source-runtime-context.js';
import {
  getCompleteShardDirectory,
  getLoadedShardDirectory,
} from './data-source-shard-directory.js';
import {
  ensureShardDocsLoaded,
  ensureShardObserved,
  ensureShardsForRowsWindow,
  getActiveWindowManifestShards,
  getExistingShardDoc,
  getExistingShardDocs,
  getObservedManifestShards,
  getShardDocIds,
  getShardedCellValue,
  getShardedProjectionCellValue,
  getShardedRowSearchText,
  getShardedRowsWindow,
  hasCompleteLoadedShardRows,
  hasLoadedShardRows,
  loadShardedRowFromExistingDocs,
  observeShardDocReady,
  queueShardedRowsRefresh,
  readLoadedShardRows,
  reconcileFileIndexObservedShardManifest,
  refreshObservedShardedRowsFromDocs,
  refreshShardedRowsFromDocs,
  requestRowShardDocLoad,
  shardedRowsEqual,
} from './data-source-shard-loading.js';
import {
  areAllManifestShardRowsMaterialized,
  captureLoadedRowShardSnapshot,
  getAuthoritativeRowShardIntegrityIssue,
  getContentReadiness,
  getInitialRowWindowReadiness,
  getLoadedRowShardIntegrityIssue,
  getManifestRowCount,
  getManifestShardsForRowsWindow,
  getRowShardHydrationProgress,
  isInitialRowWindowReady,
  materializeAllShardedRows,
  repairLoadedRowShardManifestDrift,
  scheduleLoadedEmptyRowShardConfirmation,
  scheduleLoadedRowShardIntegrityValidation,
  scheduleLoadedRowShardManifestAutoRepair,
} from './data-source-shard-readiness.js';
import {
  bindReactiveShardedCellValue,
  commitRowMutation,
  commitRowSet,
  createLocalShardEntry,
  currentWriterId,
  deleteShardedRowsFromLocalDocs,
  flushReactiveShardedCellValue,
  initializeFileIndexShardRegistry,
  normalizeShardedCellValueForProperty,
  normalizeShardedSpacialCellValue,
  provisionInitialShard,
  publishLocalShardManifest,
  queueReactiveShardedCellFlush,
  resolveLocalRows,
  resolveShardedCellForWrite,
  rowAddManyWithCellValues,
  rowAddWithId,
  serializedShardedCellEquals,
  shardedCellKey,
  updateLocalShardManifestEntry,
  writeShardedCellToLocalDoc,
  writeShardedColumnCellsToLocalDocs,
} from './data-source-shard-writing.js';

export function createDatabaseSourceRuntime(
  host: DatabaseSourceHost
): DatabaseSourceRuntime {
  // Install every operation synchronously before exposing the context.
  // Host accessors keep all mutable state in the owning data source.
  const context = host as DatabaseSourceRuntime;
  return Object.assign(context, {
    commitRowMutation: commitRowMutation.bind(context),
    commitRowSet: commitRowSet.bind(context),
    getRowWriterPort: () =>
      getDatabaseWriterPorts(context._hostModel.store.workspace).rows,
    getPublicationPort: () =>
      getDatabaseWriterPorts(context._hostModel.store.workspace).publication,
    getRelationInversePlanner: () =>
      getDatabaseWriterPorts(context._hostModel.store.workspace).inverse,
    materializeAllShardedRows: materializeAllShardedRows.bind(context),
    getLoadedShardDirectory: getLoadedShardDirectory.bind(context),
    getCompleteShardDirectory: getCompleteShardDirectory.bind(context),
    getManifestRowCount: getManifestRowCount.bind(context),
    getManifestShardsForRowsWindow:
      getManifestShardsForRowsWindow.bind(context),
    getInitialRowWindowReadiness: getInitialRowWindowReadiness.bind(context),
    getContentReadiness: getContentReadiness.bind(context),
    isInitialRowWindowReady: isInitialRowWindowReady.bind(context),
    getAuthoritativeRowShardIntegrityIssue:
      getAuthoritativeRowShardIntegrityIssue.bind(context),
    getRowShardHydrationProgress: getRowShardHydrationProgress.bind(context),
    getLoadedRowShardIntegrityIssue:
      getLoadedRowShardIntegrityIssue.bind(context),
    captureLoadedRowShardSnapshot: captureLoadedRowShardSnapshot.bind(context),
    scheduleLoadedEmptyRowShardConfirmation:
      scheduleLoadedEmptyRowShardConfirmation.bind(context),
    scheduleLoadedRowShardManifestAutoRepair:
      scheduleLoadedRowShardManifestAutoRepair.bind(context),
    scheduleLoadedRowShardIntegrityValidation:
      scheduleLoadedRowShardIntegrityValidation.bind(context),
    repairLoadedRowShardManifestDrift:
      repairLoadedRowShardManifestDrift.bind(context),
    areAllManifestShardRowsMaterialized:
      areAllManifestShardRowsMaterialized.bind(context),
    getShardDocIds: getShardDocIds.bind(context),
    getExistingShardDocs: getExistingShardDocs.bind(context),
    hasLoadedShardRows: hasLoadedShardRows.bind(context),
    hasCompleteLoadedShardRows: hasCompleteLoadedShardRows.bind(context),
    getExistingShardDoc: getExistingShardDoc.bind(context),
    ensureWorkspaceDoc: (docId: string) =>
      ensureWorkspaceDoc(context._model.store.workspace, docId),
    requestRowShardDocLoad: requestRowShardDocLoad.bind(context),
    observeShardDocReady: observeShardDocReady.bind(context),
    ensureShardDocsLoaded: ensureShardDocsLoaded.bind(context),
    ensureShardObserved: ensureShardObserved.bind(context),
    ensureShardsForRowsWindow: ensureShardsForRowsWindow.bind(context),
    readLoadedShardRows: readLoadedShardRows.bind(context),
    loadShardedRowFromExistingDocs:
      loadShardedRowFromExistingDocs.bind(context),
    shardedRowsEqual: shardedRowsEqual.bind(context),
    getShardedRowsWindow: getShardedRowsWindow.bind(context),
    queueShardedRowsRefresh: queueShardedRowsRefresh.bind(context),
    getObservedManifestShards: getObservedManifestShards.bind(context),
    getActiveWindowManifestShards: getActiveWindowManifestShards.bind(context),
    refreshObservedShardedRowsFromDocs:
      refreshObservedShardedRowsFromDocs.bind(context),
    reconcileFileIndexObservedShardManifest:
      reconcileFileIndexObservedShardManifest.bind(context),
    refreshShardedRowsFromDocs: refreshShardedRowsFromDocs.bind(context),
    getShardedProjectionCellValue: getShardedProjectionCellValue.bind(context),
    getShardedRowSearchText: getShardedRowSearchText.bind(context),
    getShardedCellValue: getShardedCellValue.bind(context),
    initializeFileIndexShardRegistry:
      initializeFileIndexShardRegistry.bind(context),
    publishLocalShardManifest: publishLocalShardManifest.bind(context),
    provisionInitialShard: provisionInitialShard.bind(context),
    createLocalShardEntry: createLocalShardEntry.bind(context),
    updateLocalShardManifestEntry: updateLocalShardManifestEntry.bind(context),
    resolveLocalRows: resolveLocalRows.bind(context),
    currentWriterId: currentWriterId.bind(context),
    writeShardedCellToLocalDoc: writeShardedCellToLocalDoc.bind(context),
    resolveShardedCellForWrite: resolveShardedCellForWrite.bind(context),
    writeShardedColumnCellsToLocalDocs:
      writeShardedColumnCellsToLocalDocs.bind(context),
    deleteShardedRowsFromLocalDocs:
      deleteShardedRowsFromLocalDocs.bind(context),
    rowAddWithId: rowAddWithId.bind(context),
    rowAddManyWithCellValues: rowAddManyWithCellValues.bind(context),
    normalizeShardedCellValueForProperty:
      normalizeShardedCellValueForProperty.bind(context),
    shardedCellKey: shardedCellKey.bind(context),
    serializedShardedCellEquals: serializedShardedCellEquals.bind(context),
    bindReactiveShardedCellValue: bindReactiveShardedCellValue.bind(context),
    queueReactiveShardedCellFlush: queueReactiveShardedCellFlush.bind(context),
    flushReactiveShardedCellValue: flushReactiveShardedCellValue.bind(context),
    normalizeShardedSpacialCellValue:
      normalizeShardedSpacialCellValue.bind(context),
    _runCapture: _runCapture.bind(context),
    getRowsWindow: getRowsWindow.bind(context),
    getProjectionSource: getProjectionSource.bind(context),
    getSearchCandidateRowIds: getSearchCandidateRowIds.bind(context),
    getCellsWindow: getCellsWindow.bind(context),
    getLogicalRowIdsSnapshot: getLogicalRowIdsSnapshot.bind(context),
    rowExists: rowExists.bind(context),
    newPropertyName: newPropertyName.bind(context),
    cellValueChange: cellValueChange.bind(context),
    cellValuesChange: cellValuesChange.bind(context),
    cellValueChangeSharded: cellValueChangeSharded.bind(context),
    resolveRawValueForProperty: resolveRawValueForProperty.bind(context),
    serializeRawValueForProperty: serializeRawValueForProperty.bind(context),
    normalizeSchemaLockedSelectValue:
      normalizeSchemaLockedSelectValue.bind(context),
    cellValueGet: cellValueGet.bind(context),
    cellValueGetFromLoadedRows: cellValueGetFromLoadedRows.bind(context),
    cellDefaultValueGet: cellDefaultValueGet.bind(context),
    normalizeRawCellValue: normalizeRawCellValue.bind(context),
    propertyAdd: propertyAdd.bind(context),
    getNormalPropertyAndIndex: getNormalPropertyAndIndex.bind(context),
    getPropertyAndIndex: getPropertyAndIndex.bind(context),
    updateProperty: updateSourceProperty.bind(context),
    propertyDataGet: propertyDataGet.bind(context),
    propertyDataSet: propertyDataSet.bind(context),
    propertyDataTypeGet: propertyDataTypeGet.bind(context),
    propertyDelete: propertyDelete.bind(context),
    collectDatabaseFileCellReviewCopyRows:
      collectDatabaseFileCellReviewCopyRows.bind(context),
    dispatchDatabaseFileCellReviewCopy:
      dispatchDatabaseFileCellReviewCopy.bind(context),
    propertyDuplicate: propertyDuplicate.bind(context),
    canMutatePropertySchema: canMutatePropertySchema.bind(context),
    isTemporalProfileRoleProperty: isTemporalProfileRoleProperty.bind(context),
    readTemporalProfileViewPath: readTemporalProfileViewPath.bind(context),
    propertyCanDelete: propertyCanDelete.bind(context),
    propertyCanDuplicate: propertyCanDuplicate.bind(context),
    propertyTypeCanSet: propertyTypeCanSet.bind(context),
    propertyMetaGet: propertyMetaGet.bind(context),
    propertyNameGet: propertyNameGet.bind(context),
    propertyNameSet: propertyNameSet.bind(context),
    propertyReadonlyGet: propertyReadonlyGet.bind(context),
    propertyTypeGet: propertyTypeGet.bind(context),
    propertyTypeSet: propertyTypeSet.bind(context),
    propertyTypeSetSharded: propertyTypeSetSharded.bind(context),
    rowAdd: rowAdd.bind(context),
    getShardedInsertIndex: getShardedInsertIndex.bind(context),
    rowDelete: rowDelete.bind(context),
    rowDeleteSharded: rowDeleteSharded.bind(context),
    rowMove: rowMove.bind(context),
    viewDataAdd: viewDataAdd.bind(context),
    viewDataDelete: viewDataDelete.bind(context),
    viewDataDuplicate: viewDataDuplicate.bind(context),
    viewDataGet: viewDataGet.bind(context),
    viewDataMoveTo: viewDataMoveTo.bind(context),
    viewDataUpdate: viewDataUpdate.bind(context),
    viewMetaGet: viewMetaGet.bind(context),
    viewMetaGetById: viewMetaGetById.bind(context),
  });
}
